import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  XeroApprovalToRowsRequest,
  XeroConnectionStatus,
  XeroEarningsRate,
  XeroEmployeeImportRequest,
  XeroEmployeeImportResult,
  XeroEmployeeSyncResult,
  XeroEmployeeSyncStatus,
  XeroLeaveTypeSyncResult,
  XeroTimesheetImportResult,
  XeroTimesheetParsedRow,
} from '../models/xero.models';
import { environment } from '../../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class XeroService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/api/xero`;

  /**
   * Get current connection status
   */
  getConnectionStatus(): Observable<XeroConnectionStatus> {
    return this.http.get<XeroConnectionStatus>(`${this.apiUrl}/auth/status`);
  }

  /**
   * Get Xero authorization URL
   * User will be redirected to this URL to authorize
   * After authorization, Xero redirects to backend callback
   * Backend processes and redirects to Angular /xero/callback route
   */
  getAuthUrl(): Observable<{ success: boolean; authUrl: string }> {
    return this.http.get<{ success: boolean; authUrl: string }>(`${this.apiUrl}/auth/connect`);
  }

  /**
   * Disconnect from Xero
   */
  disconnect(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/auth/disconnect`,
      {}
    );
  }

  /**
   * Manually refresh access token
   */
  refreshToken(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/auth/refresh`, {});
  }

  previewTimesheet(file: File): Observable<XeroTimesheetImportResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<XeroTimesheetImportResult>(`${this.apiUrl}/timesheet/preview`, form);
  }

  importTimesheet(file: File): Observable<XeroTimesheetImportResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<XeroTimesheetImportResult>(`${this.apiUrl}/timesheet/import`, form);
  }

  previewTimesheetRows(rows: XeroTimesheetParsedRow[]): Observable<XeroTimesheetImportResult> {
    return this.http.post<XeroTimesheetImportResult>(
      `${this.apiUrl}/timesheet/preview-rows`,
      rows
    );
  }

  importTimesheetRows(rows: XeroTimesheetParsedRow[]): Observable<XeroTimesheetImportResult> {
    return this.http.post<XeroTimesheetImportResult>(
      `${this.apiUrl}/timesheet/import-rows`,
      rows
    );
  }

  /** Convert selected approval-queue timesheets into Xero-shaped rows. */
  buildRowsFromApproval(
    req: XeroApprovalToRowsRequest
  ): Observable<{ success: boolean; data: XeroTimesheetParsedRow[]; count: number }> {
    return this.http.post<{ success: boolean; data: XeroTimesheetParsedRow[]; count: number }>(
      `${this.apiUrl}/timesheet/rows-from-approval`,
      req
    );
  }

  getEarningsRates(): Observable<{ success: boolean; data: XeroEarningsRate[]; count: number }> {
    return this.http.get<{ success: boolean; data: XeroEarningsRate[]; count: number }>(
      `${this.apiUrl}/timesheet/earnings-rates`
    );
  }

  previewEmployeeSync(systemUserIds?: number[]): Observable<XeroEmployeeSyncResult> {
    if (!systemUserIds || systemUserIds.length === 0) {
      return this.http.get<XeroEmployeeSyncResult>(`${this.apiUrl}/employees/preview`);
    }
    return this.http.post<XeroEmployeeSyncResult>(
      `${this.apiUrl}/employees/preview`,
      { systemUserIds }
    );
  }

  syncEmployees(systemUserIds?: number[]): Observable<XeroEmployeeSyncResult> {
    return this.http.post<XeroEmployeeSyncResult>(
      `${this.apiUrl}/employees/sync`,
      { systemUserIds: systemUserIds || [] }
    );
  }

  getEmployeeSyncStatuses(): Observable<{
    success: boolean;
    data: XeroEmployeeSyncStatus[];
    count: number;
  }> {
    return this.http.get<{
      success: boolean;
      data: XeroEmployeeSyncStatus[];
      count: number;
    }>(`${this.apiUrl}/employees/sync-status`);
  }

  // Reverse sync — pull employees from Xero into H4C

  previewImportFromXero(): Observable<XeroEmployeeImportResult> {
    return this.http.get<XeroEmployeeImportResult>(`${this.apiUrl}/employees/import-preview`);
  }

  importFromXero(req: XeroEmployeeImportRequest): Observable<XeroEmployeeImportResult> {
    return this.http.post<XeroEmployeeImportResult>(`${this.apiUrl}/employees/import`, req);
  }

  previewLeaveTypeSync(): Observable<XeroLeaveTypeSyncResult> {
    return this.http.get<XeroLeaveTypeSyncResult>(`${this.apiUrl}/leave-types/preview`);
  }

  syncLeaveTypes(): Observable<XeroLeaveTypeSyncResult> {
    return this.http.post<XeroLeaveTypeSyncResult>(`${this.apiUrl}/leave-types/sync`, {});
  }
}
